import React, { useEffect } from 'react';
import { Button, Dropdown } from 'react-bootstrap';

import RenameSessionModal from './RenameSessionModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useChatPanelStore from 'stateManagement/store/useChatPanelStore';

import {
  useCreateSession_Mutation,
  useDeleteSession_Mutation,
  usegetAllChatSessions,
} from 'stateManagement/queries/services/aiChat/aiChatQueries';

import { capitalizeFirstLetter } from 'blockscope/utils/stringUtils';

import Avatar from 'blockscope/components/common/Avatar';

const PanelHeader = () => {
  // State for managing chat sessions and selected chat
  const { data: chatSessions } = usegetAllChatSessions();
  const { mutate: deleteSession } = useDeleteSession_Mutation();
  const {
    mutate: createNewChatSession,
    isLoading: isCreatingSession,
    data: newSession,
  } = useCreateSession_Mutation();

  const chatSessionId = useChatPanelStore((state) => state.chatSessionId);
  const setChatSessionId = useChatPanelStore((state) => state.setChatSessionId);

  // Handler for new chat button
  const handleNewChat = () => {
    // Generate new chat session with unique ID and default name
    createNewChatSession(
      `Chat ${(chatSessions?.length || 0) + 1} - ${new Date(
        Date.now()
      ).toLocaleString()}`
    );
  };

  // Handler for selecting a chat session
  const handleSelectSession = (sessionId) => {
    setChatSessionId(sessionId);
  };

  // Handler for deleting a chat session
  const handleDeleteSession = (sessionId, e) => {
    e.stopPropagation(); // Prevent dropdown from closing

    // Store current session ID
    const isCurrentSession = sessionId === chatSessionId;

    // Delete the session first
    deleteSession(sessionId, {
      onSuccess: () => {
        // After successful deletion, if this was the current session, clear the selection first then refetch the data
        if (isCurrentSession) {
          setChatSessionId(null);
        }
      },
    });
  };

  // Function to truncate text
  const truncateText = (text) => {
    return text.length > 40 ? text.substring(0, 37) + '... ' : text + ' ';
  };

  useEffect(() => {
    if (chatSessions === undefined) {
      return;
    } else if (chatSessions.length === 0) {
      createNewChatSession('Chat 1');
    } else if (chatSessions.length > 0 && !chatSessionId) {
      setChatSessionId(chatSessions[chatSessions.length - 1].sessionId);
    }
  }, [chatSessions, chatSessionId]);

  useEffect(() => {
    if (newSession?.data?.sessionId) {
      // selects the new chat
      setChatSessionId(newSession.data.sessionId);
    }
  }, [newSession]);

  return (
    <div className='d-flex justify-content-between align-items-center px-1 w-100'>
      {/* New Chat Button - Small with icon only */}
      <Button
        onClick={handleNewChat}
        size='sm'
        className='d-flex align-items-center justify-content-center p-1'
        variant='falcon-primary'
        disabled={isCreatingSession}
      >
        <FontAwesomeIcon
          // @ts-ignore: fontawesome
          icon={'plus'}
          size='sm'
        />
      </Button>

      {/* Chat Sessions Dropdown with scrollable menu */}
      <div className='d-flex justify-content-between align-items-center px-1'>
        {chatSessionId ? <RenameSessionModal /> : null}
        <Dropdown>
          <Dropdown.Toggle
            variant='falcon-primary'
            id='chat-sessions-dropdown'
            size='sm'
          >
            {chatSessions?.find(
              (session) => session.sessionId === chatSessionId
            )
              ? truncateText(
                  chatSessions?.find(
                    (session) => session.sessionId === chatSessionId
                  )?.name || 'Unnamed Session'
                )
              : 'Select Session'}
          </Dropdown.Toggle>

          <Dropdown.Menu
            className='dropdown-menu-scrollable'
            style={{ maxHeight: '500px', overflowY: 'auto' }}
          >
            <Dropdown.Header>Chat Sessions</Dropdown.Header>
            {chatSessions && chatSessions.length > 0 ? (
              chatSessions.map((session) => (
                <Dropdown.Item
                  key={session.sessionId}
                  onClick={() => handleSelectSession(session.sessionId)}
                  active={chatSessionId === session.sessionId}
                >
                  <div className='d-flex align-items-center justify-content-between gap-2'>
                    {session.name}
                    <div className='d-flex align-items-center justify-content-between gap-2'>
                      <Avatar
                        name={`${capitalizeFirstLetter(
                          session.createdBy.firstName
                        )} ${capitalizeFirstLetter(
                          session.createdBy.lastName
                        )}`}
                        size={'s'}
                        className={'mx-2'}
                      />
                      <FontAwesomeIcon
                        icon='trash'
                        className='text-danger'
                        onClick={(e) =>
                          handleDeleteSession(session.sessionId, e)
                        }
                      />
                    </div>
                  </div>
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled>No chat sessions</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

export default PanelHeader;
